import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal, Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { type ImmovableAssetItemFormType, getImmovableAssetItemFormSchema } from "Forms/Schemas/Assets/immovableAssetItemFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetsOverviewQueryKey,
  getListImmovableAssetItemsQueryKey,
  useCreateImmovableAssetItem,
  useDeleteAssetItem,
  useUpdateImmovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type ImmovableAssetItem, ImmovableAssetItemImmovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { IMMOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly immovableAsset?: ImmovableAssetItem;
  readonly open: boolean;
};

const HOUSE_TYPES: string[] = [ImmovableAssetItemImmovableSubType.house, ImmovableAssetItemImmovableSubType.cabin];
const AREA_TYPES: string[] = [
  ImmovableAssetItemImmovableSubType.apartment,
  ImmovableAssetItemImmovableSubType.land,
  ImmovableAssetItemImmovableSubType.forest,
  ImmovableAssetItemImmovableSubType.other,
];
const HOUSEHOLD_INSURANCE_TYPES: string[] = [
  ImmovableAssetItemImmovableSubType.house,
  ImmovableAssetItemImmovableSubType.cabin,
  ImmovableAssetItemImmovableSubType.apartment,
];

export const ImmovableAssetItemDrawer: FC<Props> = ({ onClose, immovableAsset, open }) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListImmovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create, isPending: isCreating } = useCreateImmovableAssetItem();
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteAssetItem();
  const { mutate: update, isPending: isUpdating } = useUpdateImmovableAssetItem();

  const isMutating = isCreating || isUpdating || isDeleting;

  const immovableAssets: Option[] = (Object.keys(ImmovableAssetItemImmovableSubType) as Array<ImmovableAssetItemImmovableSubType>).map(
    (value) => ({
      value,
      name: t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType[IMMOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
    }),
  );

  const methods = useForm<ImmovableAssetItemFormType>({
    resolver: yupResolver(getImmovableAssetItemFormSchema(t)),
    mode: "onBlur",
  });

  const { control, handleSubmit, reset, watch } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        immovableAssetType: immovableAsset?.immovableSubType,
        name: immovableAsset?.name,
        value: immovableAsset?.amount.value,
        note: immovableAsset?.note ?? undefined,
        propertyInsuranceAmount: immovableAsset?.propertyInsurance?.value,
        householdInsuranceAmount: immovableAsset?.householdInsurance?.value,
        basementArea: immovableAsset?.basementArea ?? undefined,
        floorCount: immovableAsset?.floors ?? 0,
        firstFloorArea: immovableAsset?.firstFloorArea ?? undefined,
        secondFloorArea: immovableAsset?.secondFloorArea ?? undefined,
        thirdFloorArea: immovableAsset?.thirdFloorsArea ?? undefined,
        fourthFloorArea: immovableAsset?.fourthFloorArea ?? undefined,
        otherFloorArea: immovableAsset?.otherFloorsArea ?? undefined,
        area: immovableAsset?.area ?? undefined,
      };
      reset(defaultValues);
      return;
    }

    reset({});
  }, [immovableAsset, open, reset]);

  const immovableAssetType = watch("immovableAssetType");
  const floorCount = watch("floorCount") ?? 0;

  const isHouseType = HOUSE_TYPES.includes(immovableAssetType);
  const isAreaType = AREA_TYPES.includes(immovableAssetType);
  const hasHouseholdInsurance = HOUSEHOLD_INSURANCE_TYPES.includes(immovableAssetType);

  const onSubmit = (data: ImmovableAssetItemFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        immovableSubType: data.immovableAssetType as ImmovableAssetItemImmovableSubType,
        isCoveredByInsurance: !!immovableAsset?.isCoveredByInsurance,
        isUnderFinancing: !!immovableAsset?.isUnderFinancing,
        isUsableForGoals: !!immovableAsset?.isUsableForGoals,
        propertyInsurance: data.propertyInsuranceAmount
          ? { value: data.propertyInsuranceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time }
          : undefined,
        householdInsurance:
          hasHouseholdInsurance && data.householdInsuranceAmount
            ? { value: data.householdInsuranceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time }
            : undefined,
        basementArea: isHouseType ? (data.basementArea ?? undefined) : undefined,
        floors: isHouseType ? (data.floorCount ?? undefined) : undefined,
        firstFloorArea: isHouseType && floorCount > 0 ? (data.firstFloorArea ?? undefined) : undefined,
        secondFloorArea: isHouseType && floorCount > 1 ? (data.secondFloorArea ?? undefined) : undefined,
        thirdFloorsArea: isHouseType && floorCount > 2 ? (data.thirdFloorArea ?? undefined) : undefined,
        fourthFloorArea: isHouseType && floorCount > 3 ? (data.fourthFloorArea ?? undefined) : undefined,
        otherFloorsArea: isHouseType && floorCount > 4 ? (data.otherFloorArea ?? undefined) : undefined,
        area: isAreaType ? (data.area ?? undefined) : undefined,
      };

      if (!immovableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: immovableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: immovableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Deleted));
            invalidate();
            setDeleteModalOpen(false);
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Drawer
          open={open}
          onClose={onClose}
          closeLabel={t(Resources.Common.Close)}
          title={immovableAsset ? t(Resources.AnalysisDetail.Assets.Immovable.Edit) : t(Resources.AnalysisDetail.Assets.Immovable.Add)}
          onSubmit={handleSubmit(onSubmit)}
          startAction={
            !!immovableAsset && (
              <Button variant="text" color="error" disabled={readOnly || isMutating} onClick={() => setDeleteModalOpen(true)}>
                {t(Resources.Common.Delete)}
              </Button>
            )
          }
          primaryAction={
            <Button variant="contained" disabled={readOnly || isMutating} type="submit">
              {immovableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
            </Button>
          }
        >
          <Box py={Spacing.XXL}>
            <Grid container spacing={Spacing.XL}>
              <Grid size={4}>
                <FormSelect
                  control={control}
                  name="immovableAssetType"
                  label={t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType.Title)}
                  required
                  options={toSelectOptions(immovableAssets)}
                  placeholder={t(Resources.Common.SelectPlaceholder)}
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid size={8}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Name)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <FormNumberField
                  control={control}
                  name="value"
                  label={t(Resources.AnalysisDetail.Assets.Value)}
                  required
                  scale={0}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled
                  placeholder="0"
                  fullWidth
                />
              </Grid>

              <Grid size={isHouseType ? 6 : isAreaType && hasHouseholdInsurance ? 4 : 6}>
                <FormNumberField
                  control={control}
                  name="propertyInsuranceAmount"
                  label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.PropertyInsuranceAmount)}
                  valueType="text"
                  scale={0}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              {hasHouseholdInsurance && (
                <Grid size={isHouseType ? 6 : 4}>
                  <FormNumberField
                    control={control}
                    name="householdInsuranceAmount"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.HouseholdInsuranceAmount)}
                    valueType="text"
                    scale={0}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
              )}
              {isAreaType && (
                <Grid size={hasHouseholdInsurance ? 4 : 6}>
                  <FormNumberField
                    control={control}
                    name="area"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Area)}
                    valueType="text"
                    scale={0}
                    min={0}
                    thousandsSeparator=" "
                    unit="m²"
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
              )}

              {isHouseType && (
                <>
                  <Grid size={4}>
                    <FormNumberField
                      control={control}
                      name="basementArea"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.BasementArea)}
                      valueType="text"
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit="m²"
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={4}>
                    <FormSelect
                      control={control}
                      name="floorCount"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.FloorCount)}
                      options={toSelectOptions([
                        { name: "0", value: "0" },
                        { name: "1", value: "1" },
                        { name: "2", value: "2" },
                        { name: "3", value: "3" },
                        { name: "4", value: "4" },
                        { name: t(Resources.Common.Other), value: "5" },
                      ])}
                      placeholder={t(Resources.Common.SelectPlaceholder)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  {floorCount > 0 && (
                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="firstFloorArea"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 1 })}
                        valueType="text"
                        scale={0}
                        min={0}
                        thousandsSeparator=" "
                        unit="m²"
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {floorCount > 1 && (
                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="secondFloorArea"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 2 })}
                        valueType="text"
                        scale={0}
                        min={0}
                        thousandsSeparator=" "
                        unit="m²"
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {floorCount > 2 && (
                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="thirdFloorArea"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 3 })}
                        valueType="text"
                        scale={0}
                        min={0}
                        thousandsSeparator=" "
                        unit="m²"
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {floorCount > 3 && (
                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="fourthFloorArea"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NthFloorArea, { count: 4 })}
                        valueType="text"
                        scale={0}
                        min={0}
                        thousandsSeparator=" "
                        unit="m²"
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {floorCount > 4 && (
                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="otherFloorArea"
                        label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.OtherFloorArea)}
                        valueType="text"
                        scale={0}
                        min={0}
                        thousandsSeparator=" "
                        unit="m²"
                        disabled={readOnly}
                        fullWidth
                      />
                    </Grid>
                  )}
                </>
              )}

              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.Common.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Drawer>
      </FormProvider>
      <DialogModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        closeLabel={t(Resources.Common.Close)}
        title={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetTitle)}
        subtitle={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetText)}
        primaryAction={
          <Button variant="contained" disabled={readOnly || isMutating} onClick={onDelete}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </>
  );
};
